import { hasFeatureEntitlement } from '@alltrails/context/hooks/useFeatureEntitlements';
import type Experiments from '@alltrails/types/Experiments';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { User } from '@alltrails/types/User';

/**
 * Checks whether the trip planner should be surfaced at all.
 * The trip planner is not translated & is limited to en-US regardless of tier or entitlement.
 */
export const isTripPlannerVisible = (languageRegionCode?: LanguageRegionCode) => languageRegionCode === 'en-US';

/**
 * Checks whether the user is entitled to generate a trip itinerary.
 *
 * This gates creation only. Viewing a shared itinerary is available to every tier.
 */
export const canCreateTripPlan = (user?: User | null) => hasFeatureEntitlement(user ?? null, 'trip_planner');

type IsTripPlannerAvailableArgs = {
  languageRegionCode?: LanguageRegionCode;
  user?: User | null;
};

/**
 * @deprecated - Remove as part of DISCO-29421 - Prefer `isTripPlannerVisible` or `canCreateTripPlan` directly in new code.
 *
 * Combines the two checks this used to conflate: the trip planner is visible (en-US) and the user is
 * entitled to create a trip.
 */
export const isTripPlannerAvailable = ({ languageRegionCode, user }: IsTripPlannerAvailableArgs) =>
  isTripPlannerVisible(languageRegionCode) && canCreateTripPlan(user);

type IsTripPlannerEntryPointVisibleArgs = IsTripPlannerAvailableArgs & {
  experiments: Experiments;
};

/**
 * Trip planner entry points open up to every tier once `web-disco-rm-trip-planner-v2` is on — free
 * users then get the upsell once they try to create a trip.
 *
 * With the flag off, entry points stay limited to entitled users.
 */
export const isTripPlannerEntryPointVisible = ({ experiments, languageRegionCode, user }: IsTripPlannerEntryPointVisibleArgs) =>
  isTripPlannerVisible(languageRegionCode) && (experiments['web-disco-rm-trip-planner-v2']?.value === 'on' || canCreateTripPlan(user));
